import React, { useState, useEffect } from 'react';
import { Card, Switch, Slider, Select, Button, Input, Space, Typography, Divider, message, Modal, Alert } from 'antd';
import { Save, RotateCcw, Moon, Sun, Download, Upload, Settings as SettingsIcon } from 'lucide-react';
import { AppSettings, SettingsManager } from '../utils/SettingsManager';
import cacheManager from '../services/cacheManager';

const { Title, Text } = Typography;
const { Option } = Select;

interface SettingsProps {
  onSettingsChange: (settings: AppSettings) => void;
}

const Settings: React.FC<SettingsProps> = ({ onSettingsChange }) => {
  const [settings, setSettings] = useState<AppSettings>(SettingsManager.getSettings());
  const [hasChanges, setHasChanges] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    setSettings(SettingsManager.getSettings());
  }, []);

  const handleSettingChange = (key: keyof AppSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleWebSettingChange = (key: keyof AppSettings['webSettings'], value: any) => {
    const newSettings = {
      ...settings,
      webSettings: {
        ...settings.webSettings,
        [key]: value
      }
    };
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleSaveSettings = () => {
    try {
      SettingsManager.saveSettings(settings);
      onSettingsChange(settings);
      setHasChanges(false);
      message.success('设置已保存');
    } catch (error) {
      message.error('保存设置失败');
    }
  };

  const handleResetSettings = () => {
    Modal.confirm({
      title: '重置设置',
      content: '确定要重置所有设置到默认值吗？此操作不可撤销。',
      onOk: () => {
        SettingsManager.resetSettings();
        const defaultSettings = SettingsManager.getSettings();
        setSettings(defaultSettings);
        onSettingsChange(defaultSettings);
        setHasChanges(false);
        message.success('设置已重置');
      }
    });
  };

  // 云端同步
  const handleCloudSync = async () => {
    setSyncing(true);
    try {
      console.log('开始云端同步...');
      await cacheManager.syncFromCloud();
      message.success('云端同步完成');
      console.log('云端同步成功');
    } catch (error) {
      console.error('云端同步失败:', error);
      message.error('云端同步失败，请检查网络连接');
    } finally {
      setSyncing(false);
    }
  };

  const handleExportSettings = () => {
    try {
      const blob = SettingsManager.exportSettings();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-notes-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('设置已导出');
    } catch (error) {
      message.error('导出设置失败');
    }
  };

  const handleImportSettings = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const success = await SettingsManager.importSettings(file);
          if (success) {
            const importedSettings = SettingsManager.getSettings();
            setSettings(importedSettings);
            onSettingsChange(importedSettings);
            setHasChanges(false);
            message.success('设置已导入');
          } else {
            message.error('导入设置失败：文件格式不正确');
          }
        } catch (error) {
          message.error('导入设置失败');
        }
      }
    };
    input.click();
  };

  return (
    <div className="settings-container">
      <div className="settings-content">
        {/* 页面标题 */}
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <Title level={2}>
            <SettingsIcon size={24} style={{ marginRight: 8 }} />
            设置
          </Title>
          <Text type="secondary">个性化您的笔记体验</Text>
        </div>

        {/* 未保存提示 */}
        {hasChanges && (
          <Alert
            message="您有未保存的更改"
            description="请点击保存按钮来保存您的设置"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" type="primary" onClick={handleSaveSettings}>
                立即保存
              </Button>
            }
          />
        )}

        {/* 外观设置 */}
        <Card title="外观设置" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>深色模式</Text>
                <br />
                <Text type="secondary">使用深色主题保护您的眼睛</Text>
              </div>
              <Switch
                checked={settings.darkMode}
                onChange={(checked) => handleSettingChange('darkMode', checked)}
                checkedChildren={<Moon size={12} />}
                unCheckedChildren={<Sun size={12} />}
              />
            </div>

            <Divider />

            <div>
              <Text strong>字体大小</Text>
              <br />
              <Text type="secondary">调整编辑器和界面的字体大小</Text>
              <Slider
                min={12}
                max={20}
                value={settings.webSettings.fontSize}
                onChange={(value) => handleWebSettingChange('fontSize', value)}
                marks={{
                  12: '小',
                  14: '中',
                  16: '大',
                  20: '特大'
                }}
                style={{ marginTop: 8 }}
              />
            </div>

            <Divider />

            <div>
              <Text strong>行高</Text>
              <br />
              <Text type="secondary">调整文本的行间距</Text>
              <Slider
                min={1.2}
                max={2.0}
                step={0.1}
                value={settings.webSettings.lineHeight}
                onChange={(value) => handleWebSettingChange('lineHeight', value)}
                marks={{
                  1.2: '紧密',
                  1.6: '标准',
                  2.0: '宽松'
                }}
                style={{ marginTop: 8 }}
              />
            </div>
          </Space>
        </Card>

        {/* 编辑器设置 */}
        <Card title="编辑器设置" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>自动保存</Text>
                <br />
                <Text type="secondary">编辑时自动保存文档</Text>
              </div>
              <Switch
                checked={settings.autoSave}
                onChange={(checked) => handleSettingChange('autoSave', checked)}
              />
            </div>

            {settings.autoSave && (
              <>
                <Divider />
                <div>
                  <Text strong>自动保存间隔</Text>
                  <br />
                  <Text type="secondary">自动保存的时间间隔（秒）</Text>
                  <Slider
                    min={5}
                    max={60}
                    value={settings.autoSaveInterval}
                    onChange={(value) => handleSettingChange('autoSaveInterval', value)}
                    marks={{
                      5: '5秒',
                      10: '10秒',
                      30: '30秒',
                      60: '1分钟'
                    }}
                    style={{ marginTop: 8 }}
                  />
                </div>
              </>
            )}
          </Space>
        </Card>

        {/* 网页版设置 */}
        <Card title="网页版设置" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>浏览器通知</Text>
                <br />
                <Text type="secondary">允许显示浏览器通知</Text>
              </div>
              <Switch
                checked={settings.webSettings.enableNotifications}
                onChange={(checked) => handleWebSettingChange('enableNotifications', checked)}
              />
            </div>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>离线模式</Text>
                <br />
                <Text type="secondary">启用离线编辑功能</Text>
              </div>
              <Switch
                checked={settings.webSettings.enableOfflineMode}
                onChange={(checked) => handleWebSettingChange('enableOfflineMode', checked)}
              />
            </div>

            <Divider />

            <div>
              <Text strong>语言设置</Text>
              <br />
              <Text type="secondary">选择界面语言</Text>
              <Select
                value={settings.webSettings.language}
                onChange={(value) => handleWebSettingChange('language', value)}
                style={{ width: 200, marginTop: 8 }}
              >
                <Option value="zh-CN">简体中文</Option>
                <Option value="en-US">English</Option>
              </Select>
            </div>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>云端同步</Text>
                <br />
                <Text type="secondary">强制刷新所有数据缓存，与云端保持同步</Text>
              </div>
              <Button
                type="default"
                icon={<SettingsIcon size={16} />}
                onClick={handleCloudSync}
                loading={syncing}
              >
                同步数据
              </Button>
            </div>
          </Space>
        </Card>

        {/* 操作按钮 */}
        <Card title="设置管理">
          <Space wrap>
            <Button
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSaveSettings}
              disabled={!hasChanges}
            >
              保存设置
            </Button>
            <Button
              icon={<RotateCcw size={16} />}
              onClick={handleResetSettings}
            >
              重置设置
            </Button>
            <Button
              icon={<Download size={16} />}
              onClick={handleExportSettings}
            >
              导出设置
            </Button>
            <Button
              icon={<Upload size={16} />}
              onClick={handleImportSettings}
            >
              导入设置
            </Button>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
